<script setup lang="ts">
  import { onMounted, ref, watch } from 'vue';
  import { useResizeObserver } from '@vueuse/core';

  /** Grows with the text between `minRows` and `maxRows`, then scrolls. */
  interface ITextareaAutoGrow {
    minRows: number;
    maxRows: number;
  }

  const props = withDefaults(
    defineProps<{
      value?: string | null;
      placeholder?: string;
      rows?: number;
      maxlength?: number;
      disabled?: boolean;
      autoGrow?: ITextareaAutoGrow;
    }>(),
    {
      value: '',
      placeholder: undefined,
      rows: 2,
      maxlength: undefined,
      disabled: false,
      autoGrow: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'input', value: string): void;
  }>();

  const textarea = ref<HTMLTextAreaElement | null>(null);

  function onInput(ev: Event) {
    emit('input', (ev.target as HTMLTextAreaElement).value);
    resize();
  }

  /** The nearest ancestor that scrolls vertically (or the document). */
  function scrollParent(el: HTMLElement): Element | null {
    for (let node = el.parentElement; node; node = node.parentElement) {
      const overflow = getComputedStyle(node).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') return node;
    }
    return document.scrollingElement;
  }

  /** Fits the height to the content (`field-sizing` is missing on old Chrome). */
  function resize() {
    const el = textarea.value;
    // Hidden (a closed sheet): nothing to measure until it shows.
    if (!props.autoGrow || !el || !el.offsetWidth) return;

    const style = getComputedStyle(el);
    const lineHeight =
      parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
    const padding =
      parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const border =
      parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    // border-box heights (Tailwind's preflight).
    const min = lineHeight * props.autoGrow.minRows + padding + border;
    const max = lineHeight * props.autoGrow.maxRows + padding + border;

    // `height: auto` shrinks the page for a moment: keep the scroller where
    // it was, or typing at the bottom of a long form would jump.
    const scroller = scrollParent(el);
    const scrollTop = scroller?.scrollTop ?? 0;
    el.style.height = 'auto';
    const content = el.scrollHeight + border;
    el.style.height = `${Math.min(Math.max(content, min), max)}px`;
    el.style.overflowY = content > max ? 'auto' : 'hidden';
    if (scroller && scroller.scrollTop !== scrollTop)
      scroller.scrollTop = scrollTop;
  }

  // Width changes rewrap the text; showing a hidden sheet gives it a width.
  let measuredWidth = 0;
  useResizeObserver(textarea, () => {
    const width = textarea.value?.offsetWidth ?? 0;
    if (width === measuredWidth) return;
    measuredWidth = width;
    resize();
  });

  // Values set by the parent (a reset, a loaded draft) resize too.
  watch(() => props.value, resize, { flush: 'post' });
  onMounted(resize);
</script>

<template>
  <textarea
    ref="textarea"
    class="pos-ui-textarea"
    :value="value ?? ''"
    :placeholder="placeholder"
    :rows="autoGrow ? autoGrow.minRows : rows"
    :maxlength="maxlength"
    :disabled="disabled"
    @input="onInput"
  />
</template>

<style scoped lang="scss">
  .pos-ui-textarea {
    // tw-border-solid is required: Vuetify's global reset sets border-style
    // none on textarea, which would leave the tw-border width invisible.
    @apply tw-w-full tw-resize-none tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-700 tw-outline-none tw-transition-colors placeholder:tw-text-neutral-400;

    &:focus {
      @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
    }

    &:disabled {
      @apply tw-bg-neutral-50 tw-opacity-60;
    }
  }
</style>
